import { LocalStorageKeys } from '@state/persistence/localstorage/constants';
import EnvironmentConfig from '@util/config-environment';
import { safeLocalStorage } from '@util/safe-local-storage';

interface Config {
    AD_TAG_URL_DISPLAY: string;
    AD_TAG_URL_LIVE_STREAM_PREROLL: string;
    ENV: string;
    EDEN_BASE_URL: string;
    EDEN_CLIENT_ID: string;
    SNOWPLOW_APP_ID: string;
    SNOWPLOW_COLLECTOR: string;
    GOOGLE_PLACES_API_KEY: string;
    FIREBASE_API_KEY: string;
    FIREBASE_APP_ID: string;
    FIREBASE_MEASUREMENT_ID: string;
    FIREBASE_FETCH_INTERVAL_MILLIS: number;
    RECAPTCHA_SITE_KEY: string;
    UNIVERSAL_LINK_DOMAIN: string;
    STATSIG_CLIENT_API_KEY: string;
    WIDGETS_WEB_URL: string;
    TM_WEB_URL: string;
    ZENDESK_API_URL: string;
    ZENDESK_CUSTOM_FIELD_ID: string;
    ZENDESK_FORM_ID: string;
}

const ensureDefined = ([key, value]: [string, unknown]) => {
    if (value === undefined) {
        throw new Error(`Environment variable ${key} expected. Found undefined.`);
    }

    return value;
};

const stripTrailingSlashFrom = (url: string) => (url.endsWith('/') ? url.slice(0, -1) : url);

const getEdenBaseUrl = () => {
    if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
        // Config not yet initialized here
        const override = safeLocalStorage.getItem(LocalStorageKeys.EDEN_BASE_URL_OVERRIDE);
        if (override) return stripTrailingSlashFrom(override);
    }
    return stripTrailingSlashFrom(import.meta.env.VITE_EDEN_BASE_URL);
};

const Config: Config = {
    AD_TAG_URL_DISPLAY: import.meta.env.VITE_AD_TAG_URL_DISPLAY,
    AD_TAG_URL_LIVE_STREAM_PREROLL: import.meta.env.VITE_AD_TAG_URL_LIVE_STREAM_PREROLL,
    ENV: import.meta.env.VITE_ENVIRONMENT,
    EDEN_BASE_URL: getEdenBaseUrl(),
    EDEN_CLIENT_ID: import.meta.env.VITE_EDEN_CLIENT_ID,
    SNOWPLOW_APP_ID: import.meta.env.VITE_SNOWPLOW_APP_ID,
    SNOWPLOW_COLLECTOR: import.meta.env.VITE_SNOWPLOW_COLLECTOR,
    GOOGLE_PLACES_API_KEY: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
    FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
    FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
    FIREBASE_FETCH_INTERVAL_MILLIS: parseInt(import.meta.env.VITE_FIREBASE_FETCH_INTERVAL_MILLIS, 10) || -1,
    UNIVERSAL_LINK_DOMAIN: import.meta.env.VITE_UNIVERSAL_LINK_DOMAIN,
    STATSIG_CLIENT_API_KEY: import.meta.env.VITE_STATSIG_CLIENT_API_KEY,
    WIDGETS_WEB_URL: import.meta.env.VITE_WIDGETS_WEB_URL + EnvironmentConfig.PUBLIC_PREFIX,
    TM_WEB_URL: import.meta.env.VITE_TM_WEB_URL + EnvironmentConfig.PUBLIC_PREFIX,
    ZENDESK_API_URL: import.meta.env.VITE_ZENDESK_API_URL,
    ZENDESK_CUSTOM_FIELD_ID: import.meta.env.VITE_ZENDESK_CUSTOM_FIELD_ID,
    ZENDESK_FORM_ID: import.meta.env.VITE_ZENDESK_FORM_ID,
};

Object.entries(Config).forEach(ensureDefined);

export default Config;
